/**
 * Orbit — kanban de réciprocité. (Brief §3.8)
 *
 * Rappel des garde-fous, visibles dans l'UI parce qu'ils sont le produit :
 *   - l'app ne follow jamais toute seule, elle suggère
 *   - elle ne suggère jamais d'unfollow
 *   - les likes se font dans X ; ici on ne fait que les journaliser
 */

import { useCallback, useEffect, useState } from "react";
import { buildProfileUrl, type RelationshipStage } from "@rm/shared";

import { api } from "../lib/api.js";
import { Badge, Button, Card, Empty, Spinner, Stat } from "../components/ui.js";

interface Peer {
  id: number;
  handle: string;
  display_name: string | null;
  avatar_url: string | null;
  followers_count: number | null;
  stage: RelationshipStage;
  interaction_count: number;
  reciprocity_score: number | null;
  spacing: { allowed: boolean; reason: string; hoursRemaining: number };
  suggested_action: "like" | "reply" | "follow" | null;
}

interface PipelineResponse {
  buckets: Record<RelationshipStage, Peer[]>;
  stats: { pool_size: number; converted: number; conversion_rate: number };
  enabled: boolean;
}

const STAGES: { key: RelationshipStage; label: string }[] = [
  { key: "cold", label: "Cold" },
  { key: "warm", label: "Warm" },
  { key: "hot", label: "Hot" },
  { key: "converted", label: "Converted" },
];

export function OrbitPage() {
  const [data, setData] = useState<PipelineResponse | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .get<PipelineResponse>("/orbit/pipeline")
      .then(setData)
      .catch((err) => setMessage(err instanceof Error ? err.message : "Chargement impossible"));
  }, []);

  useEffect(load, [load]);

  if (!data) return message ? <Empty title="Orbit indisponible" hint={message} /> : <Spinner />;

  const total = Object.values(data.buckets).reduce((n, list) => n + list.length, 0);

  if (total === 0) {
    return (
      <div className="px-4 pt-4">
        <Empty
          title="Aucun pair dans le pool"
          hint={
            data.enabled
              ? "Le ciblage tourne à 18 h 30 et pioche parmi les comptes découverte déjà croisés par le Scout. Il lui faut donc quelques pulls avant d'avoir de la matière."
              : "Orbit est désactivé dans les réglages. Active-le pour que le ciblage commence à constituer un pool."
          }
        />
        {data.enabled && (
          <div className="flex justify-center">
            <Button
              onClick={async () => {
                setMessage("Ciblage en cours…");
                try {
                  const result = await api.post<{ evaluated: number; promoted: number }>(
                    "/orbit/target",
                  );
                  setMessage(
                    result.promoted > 0
                      ? `${result.promoted} pair(s) promu(s) sur ${result.evaluated} candidat(s).`
                      : `Aucun candidat retenu sur ${result.evaluated} examiné(s).`,
                  );
                  load();
                } catch (err) {
                  setMessage(err instanceof Error ? err.message : "Ciblage impossible");
                }
              }}
            >
              Lancer le ciblage maintenant
            </Button>
          </div>
        )}
        {message && (
          <p className="mt-3 text-center text-xs text-[var(--color-muted)]">{message}</p>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 px-4 pt-4">
      <Card>
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Pool" value={data.stats.pool_size} hint="pairs" />
          <Stat label="Convertis" value={data.stats.converted} hint="follow-backs" />
          <Stat
            label="Taux"
            value={`${Math.round(data.stats.conversion_rate * 100)} %`}
            hint="pairs engagés"
          />
        </div>
        {!data.enabled && (
          <p className="mt-3 rounded-lg bg-[#2a2410] px-3 py-2 text-xs text-[var(--color-warn)]">
            Orbit est désactivé dans les réglages : le budget reste entièrement au Scout.
          </p>
        )}
      </Card>

      {message && <p className="text-xs text-[var(--color-muted)]">{message}</p>}

      {STAGES.map(({ key, label }) => {
        const peers = data.buckets[key];
        if (peers.length === 0) return null;

        return (
          <section key={key} className="flex flex-col gap-2">
            <h2 className="flex items-center gap-2 text-sm font-medium">
              {label}
              <span className="text-xs text-[var(--color-muted)] tabular-nums">{peers.length}</span>
            </h2>

            {peers.map((peer) => (
              <Card key={peer.id} className="flex items-center gap-3">
                {peer.avatar_url ? (
                  <img src={peer.avatar_url} alt="" className="h-8 w-8 rounded-full" loading="lazy" />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-[var(--color-surface-2)]" />
                )}

                <div className="min-w-0 flex-1">
                  <a
                    href={buildProfileUrl(peer.handle)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="truncate text-sm underline-offset-2 hover:underline"
                  >
                    @{peer.handle}
                  </a>
                  <p className="text-[11px] text-[var(--color-muted)] tabular-nums">
                    {peer.followers_count ?? "?"} followers · {peer.interaction_count} interaction
                    {peer.interaction_count > 1 ? "s" : ""}
                    {peer.reciprocity_score !== null && ` · ${Math.round(peer.reciprocity_score * 100)} %`}
                  </p>
                </div>

                {key === "converted" ? (
                  <Badge tone="good">✓</Badge>
                ) : peer.spacing.allowed && peer.suggested_action ? (
                  <Button
                    onClick={async () => {
                      try {
                        await api.post("/orbit/interactions", {
                          account_id: peer.id,
                          type: peer.suggested_action,
                        });
                        setMessage(`${peer.suggested_action} journalisé pour @${peer.handle}.`);
                        load();
                      } catch (err) {
                        setMessage(err instanceof Error ? err.message : "Échec");
                      }
                    }}
                  >
                    {peer.suggested_action === "like"
                      ? "Like fait"
                      : peer.suggested_action === "reply"
                        ? "Reply faite"
                        : "Follow fait"}
                  </Button>
                ) : (
                  <span className="shrink-0 text-[11px] text-[var(--color-muted)] tabular-nums">
                    {peer.spacing.reason === "already_today"
                      ? "aujourd'hui ✓"
                      : `${peer.spacing.hoursRemaining} h`}
                  </span>
                )}
              </Card>
            ))}
          </section>
        );
      })}

      <p className="pb-4 text-[11px] text-[var(--color-muted)]">
        Les actions se font dans X. Ces boutons ne font que les journaliser, pour tenir le rythme
        d'une interaction par pair tous les deux jours. L'app ne follow ni n'unfollow jamais
        d'elle-même.
      </p>
    </div>
  );
}
